import React, { useEffect, useState, useRef } from 'react';
import { useIntl } from 'umi';
import { Justify } from 'tea-component';
import { scrollable } from 'tea-component/lib/table/addons';
import CardCommon from '@sg/components/CardCommon';
import RenderOperations from '@sg/components/Chart/Operations';
import DataProcessing from '@sg/components/DataProcessing';
import Chart from '@sg/components/Chart';
import TableContent from '@sg/components/TableContent';
import EventWindows from '@sg/components/EventWindows';
import EventSetting from '@sg/components/EventSetting';
import Loading from '@intelligence/components/Loading';
import { pickOperations } from '@sg/commonData/pickOperations';
import { xAxisHighLight } from '@sg/utils/chatUtils';
import { mathFormat } from '@sg/utils/common';
import { dealTypeValue } from '@sg/utils/useChartOptions';
import { buildTrendTooltipFormatter } from '@sg/pages/StudioManagement/buildTrendTooltipFormatter';
import { rendTitle } from '@sg/utils/commonPageMethod';
import { height } from '../../config';
import { createBtnPoint, SGBuriedBtnPoint, StudiosTab } from '@/pages/strategic/utils/buriedPoint';
import EventToggleSetting from '@/pages/strategic/components/EventToggleSetting';
import useEventSetting from '@/pages/strategic/components/EventToggleSetting/useEventSetting';
import { DefaultChat, showRevenueTrendList } from './config/chartOperations';
import { processRevenueTrendData } from './utils/processRevenueTrendData';

interface IRevenueProfitTrendCardProps {
  activeCard: string;
  revenueData: any[];
  currentMetricsData: Record<string, any>;
  trendLoading: boolean;
  studioId: string;
  studioInfo: string;
}

const RevenueProfitTrendCard: React.FC<IRevenueProfitTrendCardProps> = ({
  activeCard,
  revenueData,
  currentMetricsData,
  trendLoading,
  studioId,
  studioInfo,
}) => {
  const { formatMessage } = useIntl();
  const chartRef = useRef<any>(null);
  const [showChatsType, setShowChatsType] = useState<any>(DefaultChat);
  const [allData, setAllData] = useState<any>({});

  // Handle chart type change with buried point tracking
  const handleShowChatsTypeChange = (e: any, downloadValue?: string) => {
    // Determine trend type based on active card (Revenue or Profit)
    const trendType = activeCard === 'Profit' ? 'Profit' : 'Revenue';

    // Track buried point based on the selected value
    // For download, the second parameter is 'download' (only track when downloadValue is provided)
    // For view/line/bar/table, check e.value (but not when it's a download call)
    if (downloadValue === 'download') {
      // Only track download when the second parameter is explicitly 'download'
      // This prevents double tracking since RenderOperations calls change() twice for download
      const btnPoint = createBtnPoint(
        StudiosTab,
        'revenueTrendDownload',
        `${trendType} Trend - Download - ${studioInfo}`
      );
      if (btnPoint) {
        SGBuriedBtnPoint(btnPoint);
      }
    } else if (e?.value && e.value !== 'download') {
      // Track view/line/bar/table only when e.value is set and it's not 'download'
      const value = e.value;

      // Check if this is a view button click
      // View button has isNoSelect: true, so it toggles format but doesn't change operation
      // When view is clicked, change() is called with current operation's data, not view's data
      // We detect view clicks by checking if format changed but value stayed the same
      const isViewClick = e.format !== showChatsType?.format && value === showChatsType?.value;

      if (value === 'view' || isViewClick) {
        // Track view button click
        // For view button, we track every time format changes (toggle)
        const btnPoint = createBtnPoint(
          StudiosTab,
          'revenueTrendView',
          `${trendType} Trend - View - ${studioInfo}`
        );
        if (btnPoint) {
          SGBuriedBtnPoint(btnPoint);
        }
      } else if (value === 'line') {
        // Only track if switching to line (not already on line)
        if (showChatsType?.value !== 'line') {
          const btnPoint = createBtnPoint(
            StudiosTab,
            'revenueTrendLineChart',
            `${trendType} Trend - Line Chart - ${studioInfo}`
          );
          if (btnPoint) {
            SGBuriedBtnPoint(btnPoint);
          }
        }
      } else if (value === 'bar') {
        // Only track if switching to bar (not already on bar)
        if (showChatsType?.value !== 'bar') {
          const btnPoint = createBtnPoint(
            StudiosTab,
            'revenueTrendBarChart',
            `${trendType} Trend - Bar Chart - ${studioInfo}`
          );
          if (btnPoint) {
            SGBuriedBtnPoint(btnPoint);
          }
        }
      } else if (value === 'table') {
        // Only track if switching to table (not already on table)
        if (showChatsType?.value !== 'table') {
          const btnPoint = createBtnPoint(
            StudiosTab,
            'revenueTrendTable',
            `${trendType} Trend - Table - ${studioInfo}`
          );
          if (btnPoint) {
            SGBuriedBtnPoint(btnPoint);
          }
        }
      }
    }

    // Call the original setter
    setShowChatsType(e);
  };

  const {
    chartEventSetting,
    setChartEventSetting,
    eventSettingVisible,
    setEventSettingVisible,
    isChartReady,
    setIsChartReady,
    eventDataByGame,
    showEventsDisabled,
  } = useEventSetting({
    revenueData,
    chartRef,
    showChatsTypeValue: showChatsType?.value,
    activeCard,
    eventType: 'studios',
    studioId,
  });

  // Process revenue trend data
  useEffect(() => {
    const chartData = processRevenueTrendData(
      revenueData,
      showChatsType,
      showRevenueTrendList,
      formatMessage,
      activeCard,
      chartEventSetting.showEventBubbles
    );
    setAllData(chartData);
  }, [revenueData, showChatsType, formatMessage, activeCard, chartEventSetting.showEventBubbles]);

  return (
    <>
      {/* Revenue/Profit Trend Chart */}
      <CardCommon
        title={rendTitle({
          title: activeCard === 'Profit' ? `${formatMessage({ id: "dbs_profit_trend" })} ($)` : `${formatMessage({ id: "dbs_gross_revenue_trend" })} ($)`,
          tips:
            // Event Setting - only show for bar and line charts
            (['bar', 'line'].includes(showChatsType?.value)) && (
              <EventToggleSetting
                showEventBubbles={chartEventSetting.showEventBubbles}
                disabled={showEventsDisabled}
                onToggle={(value) => {
                  const btnPoint = createBtnPoint(
                    StudiosTab,
                    'showEvents',
                    `Show Events - ${value ? 'On' : 'Off'} - ${studioInfo}`,
                  );
                  if (btnPoint) {
                    SGBuriedBtnPoint(btnPoint);
                  }
                  setChartEventSetting({
                    ...chartEventSetting,
                    showEventBubbles: value,
                  });
                }}
                onOpenSettings={() => {
                  setEventSettingVisible(true);
                }}
              />
            ),
        })}
        className='card-common-Inertia'
        operation={
          <RenderOperations
            defaultValue={showChatsType.value}
            defaultValueShow={showChatsType.format}
            change={handleShowChatsTypeChange}
            options={pickOperations(['view', ...showRevenueTrendList, 'download'], formatMessage)}
            downloadParams={{
              ...allData.table,
              name: activeCard === 'Profit' ? formatMessage({ id: "dbs_profit_trend" }) : formatMessage({ id: "dbs_gross_revenue_trend" }),
            }}>
          </RenderOperations>
        }
      >
        {showChatsType?.value === 'bar' ? (
          <Justify
            className="dbs-justify-half"
            bottom
            left={
              <div className='dbs-revenue-now-date'>
                {(Object.keys(currentMetricsData) as Array<keyof typeof currentMetricsData>).map((item) => (
                  <div className='dbs-home-overview-item' key={item}>
                    <div className='dbs-home-overview-item-header'>
                      <div className='dbs-home-overview-item-title'>{currentMetricsData[item]?.title}</div>
                    </div>
                    <div className='dbs-revenue-date-desc'>{currentMetricsData[item]?.date}</div>
                    <DataProcessing
                      content={['$', currentMetricsData[item]?.content]}
                      data={currentMetricsData[item]?.data}
                      rate={currentMetricsData[item]?.rate || ''}
                    />
                  </div>
                ))}
              </div>
            }
          />
        ) : <></>}

        {trendLoading && (
          <Loading style={{ height: '443px' }} />
        )}

        {!trendLoading && showChatsType?.value !== 'table' && allData[showChatsType?.value] && (
          <div style={{ position: 'relative' }}>
            <Chart
              data={allData[showChatsType?.value]}
              key={`${activeCard}${showChatsType?.value}`}
              type={showChatsType?.value}
              height="443px"
              setOtherConfigFunc={(opt: any) => {
                opt.grid.containLabel = true;

                // Adjust grid top spacing for event bubbles
                if (chartEventSetting.showEventBubbles && Object.keys(chartEventSetting.bubbleEvent || {}).length > 0) {
                  opt.grid.top = 140; // Space for event bubbles
                }

                opt = xAxisHighLight(opt, {
                  type: showChatsType?.value === 'bar' ? 'shadow' : 'line',
                  lineStyle: showChatsType?.value === 'bar' ? {
                    color: 'rgba(216, 218, 228, 0.3)',
                    width: 94,
                    type: 'solid',
                  } : {
                    color: '#BBBDCB',
                    width: 1,
                    type: 'solid',
                  }
                } as any);

                // Custom tooltip formatter for Actual/Forecast/KPI series with capsule bars
                buildTrendTooltipFormatter(opt, {
                  actualName: formatMessage({ id: 'dbs_charts_legend_actual' }),
                  forecastName: formatMessage({ id: 'dbs_charts_legend_forecast' }),
                  kpiName: formatMessage({ id: 'dbs_charts_legend_kpi' }),
                  activeCard,
                  mathFormat,
                  dealTypeValue,
                });

                return opt;
              }}
              onChartReady={(chartInstance: any) => {
                chartRef.current = chartInstance;

                // Wait for chart to fully render before setting ready state
                // This ensures TopTipsEvent series is available AND convertToPixel returns valid values
                const checkChartReady = () => {
                  const chartOption = chartInstance?.getOption();
                  const hasTopTipsEvent = chartOption?.series?.some((s: any) => s.name === 'TopTipsEvent');

                  if (hasTopTipsEvent) {
                    const xAxisData = chartOption?.xAxis?.[0]?.data || [];
                    const testXAxis = xAxisData.find((x: string) => x && x.length > 0);

                    if (testXAxis) {
                      try {
                        const testPosition = chartInstance.convertToPixel(
                          { seriesName: 'TopTipsEvent' },
                          [testXAxis, 0]
                        );
                        if (!isNaN(testPosition[0])) {
                          setIsChartReady(true);
                          return;
                        }
                      } catch (e) {
                        // convertToPixel not ready yet
                      }
                    }
                  }
                  setTimeout(checkChartReady, 100);
                };

                checkChartReady();
              }}
            />
            {/* Event Windows - only show for bar and line charts */}
            {(['bar', 'line'].includes(showChatsType?.value)) && isChartReady && chartEventSetting.showEventBubbles && chartRef.current && (
              <EventWindows
                key={Object.keys(chartEventSetting.bubbleEvent || {}).join(',')}
                myChart={chartRef.current}
                eventList={chartEventSetting.bubbleEvent}
                format="YYYY-MM"
                maxTimeValue={revenueData[revenueData.length - 1]?.month || ''}
                showEventAxis={false}
                dateType="monthly"
                xAxisData={allData[showChatsType?.value]?.xAxisData || []}
                yAxisMin={false}
                topPadding={20}
              />
            )}
          </div>
        )}

        {!trendLoading && showChatsType?.value === 'table' && allData[showChatsType?.value] && (
          <TableContent
            className="dbs-commons-table dbs-chats-table"
            key={`${activeCard}${showChatsType?.value}`}
            emptyMinHeight={height}
            datas={allData[showChatsType?.value]?.data}
            columns={allData[showChatsType?.value]?.columns}
            sort={[{ by: 'fill_time', order: 'desc' }]}
            resultKey="list"
            recordKey="date"
            addons={[scrollable({})]}
          />
        )}
      </CardCommon>

      {/* Event Setting Modal */}
      <EventSetting
        visible={eventSettingVisible}
        close={() => setEventSettingVisible(false)}
        confirm={(params: any) => {
          setChartEventSetting((prev: any) => ({
            ...prev,
            bubbleEvent: params.bubbleEvent,
          }));
          setEventSettingVisible(false);
        }}
        data={eventDataByGame}
        timeFormat="YYYY-MM"
        dateType="monthly"
        xAxisData={allData[showChatsType?.value]?.xAxisData?.filter((x: string) => x !== '') || []}
      />
    </>
  );
};

export default RevenueProfitTrendCard;
